import {
  isEmptyObj,
  getMapView,
  updateGraphicLayer,
  updateLabelLayer,
  updateColorLayer,
  updateLabelLayerSettings,
  markRooms,
  zoomRrn,
  zoomBrn,
  zoomBbox,
  zoomFeature,
  setMapFilter,
  setPointFloorFilter,
  getPrintStyleHTMLTag,
  resetPrintMode,
  cursorPointer,
  cursorCrosshair,
  cursorReset,
  removeMarkersForPointFeatures,
  drawRectToRooms,
  markRoomCustomColor,
  addMarker,
  markActiveRoom,
  markActiveBuilding,
  markActiveFloor,
  hideBuildingsExcept,
  revertHideBuildingsExcept,
  updateRoomSelectLayer,
  setPrintMode,
} from "./maputils";
import { getAllMarkers } from "../Shared/markerStore";
import {
  actionDrawUpdateGroupFeature,
  actionDrawToggleVisiblePolygonsFromFloorChange,
  initDraw,
} from "../Draw";
import { actionDataSelectFeatureRequested } from "../Data";
import { actionMapviewIgnoreUpdated } from "../App";

export const handleMapViewChange = (map, mapviewChangeRequest, mapPadding) => {
  if (!map || isEmptyObj(mapviewChangeRequest)) {
    return;
  }
  const defaults = { duration: 250, speed: 1.2 };
  const mapView = getMapView(map);
  const combined = { ...defaults, ...mapView, ...mapviewChangeRequest };
  if (mapviewChangeRequest.fromSlider) {
    // We override the defaults for the zoom/pitch/rotation sliders to make it
    // more quickly track the current slide position.
    combined.duration = 10;
    combined.speed = 10;
  }
  const { zoom, bearing, pitch, center, duration, speed } = combined;

  if (mapviewChangeRequest.center) {
    map.flyTo({
      zoom,
      pitch,
      bearing,
      center,
      speed,
      padding: mapPadding,
      essential: true,
    });
  } else {
    map.easeTo({
      zoom,
      pitch,
      bearing,
      center,
      duration,
      padding: mapPadding,
      essential: true,
    });
  }
};

export const handleSourceLayersAdd = (map, initialSourceLayers) => {
  if (!map || !initialSourceLayers.sources || !initialSourceLayers.layers) {
    return;
  }
  const { sources, layers } = initialSourceLayers;
  sources.forEach((source) => {
    if (source.name === "campus-room") {
      source.data.promoteId = "rmrecnbr";
    }
  });
  sources.forEach((source) => map.addSource(source.name, source.data));
  layers.forEach((layer) => map.addLayer(layer));
  // draw must be added after default layers, otherwise it'll be under building polygons
  initDraw(map);
};

export function handleLayerChange(map, layerChangeRequest, color) {
  if (!map || isEmptyObj(layerChangeRequest)) {
    return;
  }
  const { type, id, data, layer, targetColorDef, field, value, labels } =
    layerChangeRequest;

  if (Array.isArray(layerChangeRequest)) {
    handleLayerChangeHelper(map, layerChangeRequest, color);
  }

  if (type === "graphic") {
    updateGraphicLayer(map, data, layer, color);
  } else if (type === "label" && labels?.length) {
    labels.forEach(({ data, layer }) => updateLabelLayer(map, data, layer));
  } else if (type === "label") {
    updateLabelLayer(map, data, layer);
  } else if (type === "color") {
    updateColorLayer(map, targetColorDef, data);
  } else if (type === "label-settings") {
    updateLabelLayerSettings(map, field, value);
  } else if (type === "room-select") {
    updateRoomSelectLayer(map, id, field, value);
  }
}

function handleLayerChangeHelper(map, layerChangeRequests, color) {
  layerChangeRequests.forEach((layerChangeRequest) => {
    handleLayerChange(map, layerChangeRequest, color);
  });
}

export function handleSelectFeatures(map, features) {
  if (!map || isEmptyObj(features)) {
    return;
  }

  const { type, data, layerId } = features;
  if (type === "room") {
    markRooms(map, data, layerId);
  } else if (type === "room-color") {
    markRoomCustomColor(map, data, layerId);
  }
}

// zoom to features
// @param zoomFeatures {data, type} - data:[rrn1,...]/rrn/brn/[feature1,...]/feature type:rrn/brn/bbox/null
// @param mapPadding {top, bottom, left, right}
export const handleZoomFeatures =
  (map, zoomFeatures, mapPadding, idToken) => () => {
    const { data, type } = zoomFeatures || {};
    const isValid = map && map.fitBounds && data;
    if (!isValid) {
      return;
    }

    // @todo: if drawing, dont zoom
    if (type === "rrn") {
      zoomRrn(map, data, mapPadding, idToken);
    } else if (type === "brn") {
      zoomBrn(map, data, mapPadding, idToken);
    } else if (type === "bbox") {
      zoomBbox(map, data, mapPadding);
    } else {
      zoomFeature(map, data, mapPadding);
    }
  };

export function handleFloorChange(map, floor, dispatch, singleBuildingMode) {
  if (!map || !floor) {
    return;
  }
  const floorFilter = ["==", "floor", floor];
  const LAYER_ARRAY = [
    "umich-floor",
    "umich-floor-active",
    "umich-room",
    "umich-room-avail",
    "umich-room-shared",
    "umich-room-select-1",
    "umich-room-select-2",
    "umich-room-select-3",
    "umich-room-select-4",
    "umich-room-select-5",
    "umich-room-select-intersect",
    "umich-room-select-symbol-1",
    "umich-room-draw-1",
    "umich-reference",
    "umich-room-label",
  ];
  LAYER_ARRAY.forEach((layer) =>
    setMapFilter(map, layer, "floor", floorFilter),
  );
  setMapFilter(map, "umich-room-active", "rmrecnbr", ["in", "rmrecnbr"]);

  const customLayers = map
    .getStyle()
    .layers.map((layer) => layer.id)
    .filter((id) => id.indexOf("sheet") === 0);
  customLayers.forEach((layer) => {
    if (layer.indexOf("sheet-point") === 0) {
      setPointFloorFilter(map, layer, floor);
    } else {
      setMapFilter(map, layer, "floor", floorFilter);
    }
  });

  // toggle markers
  const markers = getAllMarkers();
  for (let markerId in markers) {
    const marker = markers[markerId];
    const hide =
      marker &&
      marker.properties &&
      marker.properties.floor &&
      marker.properties.floor !== floor;
    const singleBuildingHide =
      marker &&
      marker.properties &&
      singleBuildingMode &&
      singleBuildingMode.bldrecnbr &&
      (!marker.properties.bldrecnbr ||
        marker.properties.bldrecnbr !== singleBuildingMode.bldrecnbr);
    if (hide || singleBuildingHide) {
      marker.remove();
    } else {
      marker.addTo(map);
    }
  }

  // toggle drawn polygons visibility
  dispatch(actionDrawToggleVisiblePolygonsFromFloorChange(map, floor));
}

function mapResizeNeeded(map) {
  const container = map.getContainer();
  return (
    map.transform.width !== container.clientWidth ||
    map.transform.height !== container.clientHeight
  );
}

function mapResizeIfNeededNoHistory(dispatch, map) {
  // We don't want to track this change in our previous/next history.
  // If we send a +1 when no resize is needed, it'll cause the NEXT
  // actual change to not get recorded in history.
  if (mapResizeNeeded(map)) {
    dispatch(actionMapviewIgnoreUpdated(+1));
    map.resize();
  }
}

export function handlePrint(map, mapContainer, dispatch, print, screenSize) {
  if (!map || !mapContainer || !mapContainer.current || isEmptyObj(print)) {
    return;
  }
  const { status } = print;
  const styleTag = getPrintStyleHTMLTag();

  switch (status) {
    case "print":
      window.print();
      break;

    case "cancel":
      resetPrintMode(styleTag, document.body, mapContainer.current, screenSize);
      mapResizeIfNeededNoHistory(dispatch, map);
      break;

    case "preview":
      setPrintMode(styleTag, document.body, mapContainer.current, print);
      mapResizeIfNeededNoHistory(dispatch, map);
      break;
  }
}

export const handleCursorChange = (map, cursor) => () => {
  if (!map || !cursor) {
    return;
  }

  if (cursor.drawMode) {
    cursorCrosshair(map);
    return;
  }

  if (cursor.featureHover) {
    cursorPointer(map);
    return;
  }

  cursorReset(map);
};

export const handleDrawModeChange = (map, dispatch, drawMode) => () => {
  if (!map || !map.draw || !drawMode) {
    return;
  }

  const { mode, data, clearDrawing, clearActiveDrawing } = drawMode;
  const validModes = new Set([
    "draw_line_string",
    "draw_polygon",
    "draw_point",
    "simple_select",
    "draw_rectangle",

    // See comments in settings.js to understand why we purposefully
    // allow the drawing library to enter this mode.
    "room_picker",
  ]);

  if (clearDrawing) {
    if (!data) {
      return;
    }
    if (typeof data === "string") {
      removeMarkersForPointFeatures(data);
      map.draw.delete(data);
    }
    if (Array.isArray(data)) {
      removeMarkersForPointFeatures(data);
      data.forEach((id) => map.draw.delete(id));
    }
    return;
  }

  if (clearActiveDrawing) {
    // This is safer than calling map.draw.trash() because
    // that would trash any previously-created drawings
    // that are "selected" or in "direct_select" mode.
    // For example, if you click on an established rectangle,
    // it will "select" it. If you click it again, you
    // enter "direct_select" mode where you can drag
    // individual points around.
    //
    // If we did map.draw.trash(), it would erase any
    // drawings that were in one of these states.
    //
    // Here we identify and delete only drawings which
    // do not yet have a "drawmode" attribute. These
    // are drawings that have been started but not
    // actually created yet.
    const allDrawings = map.draw.getAll();
    for (let drawing of allDrawings.features) {
      if (!drawing.properties.drawmode) {
        map.draw.delete([drawing.id]);
      }
    }
  }

  if (validModes.has(mode)) {
    map.draw.changeMode(mode);
    return;
  }

  if (mode === "draw_zoom_box") {
    map.draw.changeMode("draw_rectangle");
    return;
  }

  // @todo: hide by groupId
  // @data: {featureGroup}
  if (mode === "hide_feature_group") {
    const { features } = data;
    const featureIds = features.map((feat) => feat.id);
    // hide draw features
    map.getStyle().layers.forEach((layer) => {
      if (layer.id.indexOf("draw") !== -1) {
        map.setLayoutProperty(layer.id, "visibility", "none");
      }
    });
    // hide markers
    const markers = getAllMarkers();
    for (let markerId in markers) {
      if (featureIds.includes(markerId)) {
        markers[markerId].remove();
      }
    }
    // hide selected rooms
    dispatch(
      actionDataSelectFeatureRequested({
        type: "room-color",
        data: [],
        layerId: "umich-room-draw-1",
      }),
    );

    return;
  }

  // @todo: show by groupId
  // @data: {featureGroup}
  if (mode === "show_feature_group") {
    const { features } = data;
    const featureIds = features.map((feat) => feat.id);
    // show draw features
    map.getStyle().layers.forEach((layer) => {
      if (layer.id.indexOf("draw") !== -1) {
        map.setLayoutProperty(layer.id, "visibility", "visible");
      }
    });
    // show markers
    const markers = getAllMarkers();
    for (let markerId in markers) {
      if (featureIds.includes(markerId)) {
        markers[markerId].addTo(map);
      }
    }
    // show selected rooms
    dispatch(
      actionDataSelectFeatureRequested({
        type: "room-color",
        data: features,
        layerId: "umich-room-draw-1",
      }),
    );
    return;
  }

  if (mode === "group_room_picker") {
    const isStepTwo = data && data.length;
    if (!isStepTwo) {
      // step 1: draw rectangle over map
      map.draw.changeMode("draw_rectangle");
    } else {
      // step 2: query rooms below rectangle, store rooms
      // drawRedux will send back here with rectangle feature via var data
      const [feature] = data;
      const rooms = drawRectToRooms(map, feature) || [];
      const visitedRooms = {};
      const uniqRooms = rooms.reduce((agg, room) => {
        let rmrecnbr = room && room.properties && room.properties.rmrecnbr;
        if (rmrecnbr && !visitedRooms[rmrecnbr]) {
          agg.push(room);
          visitedRooms[rmrecnbr] = true;
        }
        return agg;
      }, []);
      const features = uniqRooms.map((feature) => {
        const properties = {
          ...feature.properties,
          drawmode: "group_room_picker",
        };
        const geometry = { ...feature._geometry, ...feature.geometry };
        return { ...feature, geometry, properties };
      });
      map.draw.changeMode("simple_select");
      dispatch(actionDrawUpdateGroupFeature("add", features));
      map.draw.delete(feature.id);
    }
  }
};

export const handleDrawFeaturesChange = (map, drawFeatures) => () => {
  if (!map || !map.draw || !drawFeatures) {
    return;
  }

  // update draw feature propertiesi
  drawFeatures.forEach((feature) => {
    const drawFeature = map.draw.get(feature.id);
    if (!drawFeature) {
      return;
    }

    for (let property in feature.properties) {
      // only update properties that changed
      if (drawFeature.properties[property] === feature.properties[property]) {
        continue;
      }
      map.draw.setFeatureProperty(
        feature.id,
        property,
        feature.properties[property],
      );

      // update marker
      if (property === "color" && feature.geometry.type === "Point") {
        removeMarkersForPointFeatures(feature.id);
        addMarker(map, feature);
      }
    }
  });

  // refresh draw layer to reflect current changes in color
  const collections = map.draw.getAll();
  map.draw.set(collections);
};

export const handleActiveFeatureChange = (map, activeFeature) => () => {
  if (!map || !map.draw || !activeFeature) {
    return;
  }

  const { bldrecnbr, floor, rmrecnbr } = activeFeature;
  markActiveBuilding(map, bldrecnbr);
  markActiveFloor(map, bldrecnbr, floor);
  markActiveRoom(map, rmrecnbr);
};

export const handleSingleBuildingMode = (map, singleBuildingMode) => () => {
  if (!map || !singleBuildingMode) {
    return;
  }

  if (singleBuildingMode && singleBuildingMode.bldrecnbr) {
    // single mode on
    hideBuildingsExcept(map, singleBuildingMode.bldrecnbr);
  } else if (singleBuildingMode && !singleBuildingMode.bldrecnbr) {
    // single mode off
    revertHideBuildingsExcept(map);
  }
};

function setCameraIconStyle(map, labelsAreVisible) {
  // If there are any labels, move the camera icon "up" a bit in hopes of making
  // the text labels more readable.
  map.setLayoutProperty(
    "umich-room-select-symbol-1",
    "icon-offset",
    labelsAreVisible ? [0, -25] : [0, 0],
  );
}

export const handleIconAndLabelOffset = (map, label) => () => {
  if (
    map &&
    map.getLayer("umich-room-label") &&
    map.getLayer("umich-room-select-symbol-1")
  ) {
    const labelsAreVisible = Object.values(label).some((e) => e);
    setCameraIconStyle(map, labelsAreVisible);
  }
};
